import React, { useState } from 'react';
import { User, Mail, Phone, MapPin, Calendar, Clock, CreditCard, Send } from 'lucide-react';

const Inscription = () => {
  const [formData, setFormData] = useState({
    civilite: '',
    prenom: '',
    nom: '',
    email: '',
    telephone: '',
    adresse: '',
    ville: '',
    codePostal: '',
    formation: '',
    duree: '',
    mois: '',
    lieu: '',
    financement: '',
    haccp: false,
    message: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setFormData({
        ...formData,
        [name]: (e.target as HTMLInputElement).checked
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Création du contenu de l'email
    const emailSubject = encodeURIComponent('Demande d\'inscription - École de la Pizza italienne');
    const emailBody = encodeURIComponent(
      `Bonjour,\n\n` +
      `Je souhaite m'inscrire à une formation à l'École de la Pizza italienne.\n\n` +
      `MES INFORMATIONS :\n` +
      `- Civilité : ${formData.civilite}\n` +
      `- Nom : ${formData.nom}\n` +
      `- Prénom : ${formData.prenom}\n` +
      `- Email : ${formData.email}\n` +
      `- Téléphone : ${formData.telephone}\n` +
      `- Adresse : ${formData.adresse}\n` +
      `- Ville : ${formData.ville}\n` +
      `- Code Postal : ${formData.codePostal}\n\n` +
      `FORMATION SOUHAITÉE :\n` +
      `- Formation : ${formData.formation}\n` +
      `- Durée : ${formData.duree}\n` +
      `- Mois souhaité : ${formData.mois}\n` +
      `- Lieu : ${formData.lieu}\n` +
      `- Formation HACCP : ${formData.haccp ? 'Oui' : 'Non'}\n` +
      `- Mode de financement : ${formData.financement}\n\n` +
      `MESSAGE :\n${formData.message}\n\n` +
      `Cordialement`
    );
    
    // Ouverture du client email
    const mailtoLink = `mailto:ecoledelapizza@gmail.com?subject=${emailSubject}&body=${emailBody}`;
    window.location.href = mailtoLink;
    
    // Message de confirmation
    alert('Votre client email va s\'ouvrir avec votre demande d\'inscription pré-remplie. Envoyez l\'email pour finaliser votre demande.');
  };

  return (
    <section id="inscription" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Inscription aux Formations
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Rejoignez notre école et transformez votre passion en expertise professionnelle. 
            Remplissez le formulaire ci-dessous pour débuter votre parcours.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            {/* Header du formulaire */}
            <div className="bg-gradient-to-r from-red-600 to-red-800 p-8 text-white text-center">
              <h3 className="text-3xl font-bold mb-2">Formulaire d'Inscription</h3>
              <p className="text-red-100">Participez à la formation de votre choix</p>
            </div>

            <form onSubmit={handleSubmit} className="p-8 space-y-8">
              {/* Informations Personnelles */}
              <div>
                <h4 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <User className="h-6 w-6 text-red-600" />
                  Informations Personnelles
                </h4>
                
                <div className="grid md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label htmlFor="civilite" className="block text-sm font-medium text-gray-700 mb-2">
                      Civilité *
                    </label>
                    <select
                      id="civilite"
                      name="civilite"
                      required
                      value={formData.civilite}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    >
                      <option value="">Sélectionnez</option>
                      <option value="M">Monsieur</option>
                      <option value="Mme">Madame</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="prenom" className="block text-sm font-medium text-gray-700 mb-2">
                      Prénom *
                    </label>
                    <input
                      type="text"
                      id="prenom"
                      name="prenom"
                      required
                      value={formData.prenom}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label htmlFor="nom" className="block text-sm font-medium text-gray-700 mb-2">
                      Nom *
                    </label>
                    <input
                      type="text"
                      id="nom"
                      name="nom"
                      required
                      value={formData.nom}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      <Mail className="h-4 w-4 inline mr-1" />
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label htmlFor="telephone" className="block text-sm font-medium text-gray-700 mb-2">
                      <Phone className="h-4 w-4 inline mr-1" />
                      Téléphone *
                    </label>
                    <input
                      type="tel"
                      id="telephone"
                      name="telephone"
                      required
                      value={formData.telephone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label htmlFor="adresse" className="block text-sm font-medium text-gray-700 mb-2">
                    <MapPin className="h-4 w-4 inline mr-1" />
                    Adresse postale
                  </label>
                  <input
                    type="text"
                    id="adresse"
                    name="adresse"
                    value={formData.adresse}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="ville" className="block text-sm font-medium text-gray-700 mb-2">
                      Ville
                    </label>
                    <input
                      type="text"
                      id="ville"
                      name="ville"
                      value={formData.ville}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label htmlFor="codePostal" className="block text-sm font-medium text-gray-700 mb-2">
                      Code Postal
                    </label>
                    <input
                      type="text"
                      id="codePostal"
                      name="codePostal"
                      value={formData.codePostal}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Choix de Formation */}
              <div>
                <h4 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <Calendar className="h-6 w-6 text-red-600" />
                  Choix de Formation
                </h4>

                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="formation" className="block text-sm font-medium text-gray-700 mb-2">
                      Formation souhaitée *
                    </label>
                    <select
                      id="formation"
                      name="formation"
                      required
                      value={formData.formation}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    >
                      <option value="">Sélectionnez une formation</option>
                      <option value="expert-napolitaine">Expert Pizza Napolitaine (1800€)</option>
                      <option value="pizzaiolo-base">Formation Pizzaïolo (1000€)</option>
                      <option value="cqp">CQP Pizzaïolo (2490€)</option>
                      <option value="haccp">Hygiène Alimentaire HACCP (350€)</option>
                      <option value="creation-entreprise">Création d'Entreprise (650€)</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="duree" className="block text-sm font-medium text-gray-700 mb-2">
                      <Clock className="h-4 w-4 inline mr-1" />
                      Durée
                    </label>
                    <select
                      id="duree"
                      name="duree"
                      value={formData.duree}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    >
                      <option value="">Sélectionnez</option>
                      <option value="35h">35 heures</option>
                      <option value="40h">40 heures</option>
                      <option value="70h">70 heures</option>
                    </select>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="mois" className="block text-sm font-medium text-gray-700 mb-2">
                      Mois souhaité
                    </label>
                    <select
                      id="mois"
                      name="mois"
                      value={formData.mois}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    >
                      <option value="">Sélectionnez</option>
                      <option value="janvier">Janvier</option>
                      <option value="fevrier">Février</option>
                      <option value="mars">Mars</option>
                      <option value="avril">Avril</option>
                      <option value="mai">Mai</option>
                      <option value="juin">Juin</option>
                      <option value="juillet">Juillet</option>
                      <option value="aout">Août</option>
                      <option value="septembre">Septembre</option>
                      <option value="octobre">Octobre</option>
                      <option value="novembre">Novembre</option>
                      <option value="decembre">Décembre</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="lieu" className="block text-sm font-medium text-gray-700 mb-2">
                      Lieu de formation
                    </label>
                    <select
                      id="lieu"
                      name="lieu"
                      value={formData.lieu}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    >
                      <option value="">Sélectionnez</option>
                      <option value="antibes">Antibes</option>
                      <option value="vallauris">Vallauris</option>
                    </select>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      name="haccp"
                      checked={formData.haccp}
                      onChange={handleChange}
                      className="w-5 h-5 text-red-600 border-gray-300 rounded focus:ring-red-500"
                    />
                    <span className="text-gray-700">
                      Ajouter la formation HACCP (Hygiène Alimentaire) - 350€
                    </span>
                  </label>
                </div>
              </div>

              {/* Financement */}
              <div>
                <h4 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <CreditCard className="h-6 w-6 text-red-600" />
                  Mode de Financement
                </h4>

                <div>
                  <label htmlFor="financement" className="block text-sm font-medium text-gray-700 mb-2">
                    Comment souhaitez-vous financer votre formation ?
                  </label>
                  <select
                    id="financement"
                    name="financement"
                    value={formData.financement}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  >
                    <option value="">Sélectionnez</option>
                    <option value="cpf">CPF (Compte Personnel de Formation)</option>
                    <option value="pole-emploi">Pôle Emploi</option>
                    <option value="opco">OPCO (AKTO/FAFIH)</option>
                    <option value="agefice">AGEFICE</option>
                    <option value="fifpl">FIFPL</option>
                    <option value="personnel">Financement personnel</option>
                    <option value="entreprise">Prise en charge entreprise</option>
                  </select>
                </div>

                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Information :</strong> Pour tous les financements, nous vous accompagnons dans vos démarches. 
                    Contactez-nous au 06 60 47 67 40 ou par email à ecoledelapizza@gmail.com pour plus d'informations.
                  </p>
                </div>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Message ou questions particulières
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Parlez-nous de votre projet, vos attentes, ou posez vos questions..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                ></textarea>
              </div>

              {/* Submit Button */}
              <div className="text-center">
                <button
                  type="submit"
                  className="bg-red-600 text-white px-12 py-4 rounded-lg hover:bg-red-700 transition-colors font-semibold text-lg flex items-center justify-center gap-2 mx-auto"
                >
                  <Send className="h-5 w-5" />
                  Envoyer ma Demande d'Inscription
                </button>
                <p className="text-sm text-gray-500 mt-4">
                  Nous vous recontacterons sous 24h pour finaliser votre inscription
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Inscription;